import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import OtherPayablesForm from './OtherPayablesForm';
import OfficePayablesForm from './OfficePayablesForm';
import { usePermissions } from '../../services/PermissionsContext';
import Select2Dropdown from '../common/Select2Dropdown';
import PopoutWindow from '../common/PopoutWindow';
import useSortableTable from '../common/useSortableTable';

const API_BASE_URL = '/api/admin/other-payables/';

const formatAmountInput = (raw) => {
    if (!raw && raw !== 0) return '';
    const str = String(raw).replace(/[^0-9.]/g, '');
    const parts = str.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
};

const STATUS_LABELS = {
    'PENDING': 'Pending',
    'APPROVED 1': 'Approved 1',
    'APPROVED 2': 'Approved 2',
    'APPROVED 3': 'Approved 3',
    'CLIENT PAID': 'Client Paid',
    'REJECTED': 'Rejected'
};

const MODULE_PERMISSION = {
    'PERMIT': 'PERMIT_PAYABLES',
    'PORT': 'PORT_PAYABLES',
    'DOCUMENTATION': 'DOC_PAYABLES',
    'ACCOUNTS': 'PAYABLES',
};

const MODULE_PREFIX = {
    'PERMIT': 'PD',
    'PORT': 'OD',
    'DOCUMENTATION': 'DD',
    'ACCOUNTS': 'OE',
};

const MODULE_LABEL = {
    'PERMIT': 'Permit',
    'PORT': 'Port',
    'DOCUMENTATION': 'Documentation',
    'ACCOUNTS': 'Accounts',
};

const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
});

const OtherPayablesList = ({ module = 'PERMIT', hideNewPayable = false }) => {
    const [payables, setPayables] = useState([]);
    const [activeTab, setActiveTab] = useState(module === 'ACCOUNTS' ? 'cash_office' : 'all');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchField, setSearchField] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [tabCounts, setTabCounts] = useState({ all: 0, pending_receipts: 0, cash_office: 0 });
    const [searchPayableHasSearched, setSearchPayableHasSearched] = useState(false);
    const [searchPayableField, setSearchPayableField] = useState('');
    const [searchPayableQuery, setSearchPayableQuery] = useState('');
    const [lastSearchPayableParams, setLastSearchPayableParams] = useState({ field: '', query: '' });

    const searchOptions = [
        { id: 'reference_no', text: 'Ref No' },
        { id: 'job_number', text: 'Job ID' },
        { id: 'idf_number', text: 'IDF #' },
        { id: 'bl_number', text: 'BL #' },
        { id: 'vendor_name', text: 'Vendor' },
        { id: 'customer_name', text: 'Customer' }
    ];

    // One entry per open popout window — unique key guarantees a distinct
    // browser window (window.open reuses a window when name matches).
    const [formPopouts, setFormPopouts] = useState([]);       // { key, mode, payable }
    const [officePopouts, setOfficePopouts] = useState([]);   // { key, mode, payable }
    const [showContraModal, setShowContraModal] = useState(false);
    const [contraForm, setContraForm] = useState({ outgoing_bank: '', incoming_bank: '', date: '', amount: '', tax_invoice_no: '', tax_invoice_file: null });
    const [bankAccounts, setBankAccounts] = useState([]);

    const { hasPermission } = usePermissions();

    const handleRefresh = () => {
        if (activeTab === 'search_payable') {
            if (searchPayableHasSearched) {
                fetchPayables('search_payable', lastSearchPayableParams);
            }
            return;
        }
        fetchPayables(activeTab);
    };

    const permissionModule = MODULE_PERMISSION[module] || 'PERMIT_PAYABLES';
    const moduleLabel = MODULE_LABEL[module] || module;

    const fetchPayables = useCallback(async (tab, searchParams = {}) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (tab === 'cash_office') {
                const prefix = MODULE_PREFIX[module];
                if (prefix) params.set('reference_prefix', prefix);
                params.set('module', 'OFFICE');
                params.set('status', 'PENDING,APPROVED 1,APPROVED 2,REJECTED');
            } else {
                params.set('module', module);
                if (tab === 'all') {
                    params.set('status', 'PENDING,APPROVED 1,APPROVED 2,REJECTED');
                } else if (tab === 'pending_receipts') {
                    params.set('status', 'APPROVED 3,CLIENT PAID');
                    params.set('receipt_uploaded', 'false');
                    if (module === 'ACCOUNTS') {
                        params.set('module', 'OFFICE');
                        const prefix = MODULE_PREFIX[module];
                        if (prefix) params.set('reference_prefix', prefix);
                    }
                } else if (tab === 'search_payable') {
                    if (searchParams.field) params.set('search_field', searchParams.field);
                    if (searchParams.query) params.set('search', searchParams.query);

                    // Also fetch OFFICE module payables for this department in parallel
                    const officeParams = new URLSearchParams(params);
                    officeParams.set('module', 'OFFICE');
                    const prefix = MODULE_PREFIX[module];
                    if (prefix) officeParams.set('reference_prefix', prefix);

                    const queryString = params.toString() ? `?${params.toString()}` : '';
                    const officeQueryString = `?${officeParams.toString()}`;
                    const [response, officeResponse] = await Promise.all([
                        fetch(`${API_BASE_URL}${queryString}`, { headers: getAuthHeaders() }),
                        fetch(`${API_BASE_URL}${officeQueryString}`, { headers: getAuthHeaders() }),
                    ]);
                    if (!response.ok) throw new Error('Failed to fetch payables');
                    const data = await response.json();
                    const officeData = officeResponse.ok ? await officeResponse.json() : [];
                    const merged = [...data, ...officeData].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    setPayables(merged);
                    return;
                }
            }

            const queryString = params.toString() ? `?${params.toString()}` : '';
            const response = await fetch(`${API_BASE_URL}${queryString}`, {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to fetch payables');
            const data = await response.json();
            setPayables(data);
            if (tab === 'all') {
                setTabCounts(prev => ({ ...prev, all: data.length }));
            } else if (tab === 'pending_receipts') {
                setTabCounts(prev => ({ ...prev, pending_receipts: data.length }));
            }
        } catch (err) {
            setError(err.message);
            setPayables([]);
        } finally {
            setLoading(false);
        }
    }, [module]);

    const fetchTabCount = useCallback(async (tab) => {
        try {
            const params = new URLSearchParams();
            params.set('count_only', 'true');
            if (tab === 'cash_office') {
                const prefix = MODULE_PREFIX[module];
                if (prefix) params.set('reference_prefix', prefix);
                params.set('module', 'OFFICE');
                params.set('status', 'PENDING,APPROVED 1,APPROVED 2,REJECTED');
            } else {
                params.set('module', module);
                if (tab === 'pending_receipts') {
                    params.set('status', 'APPROVED 3,CLIENT PAID');
                    params.set('receipt_uploaded', 'false');
                    if (module === 'ACCOUNTS') {
                        params.set('module', 'OFFICE');
                        const prefix = MODULE_PREFIX[module];
                        if (prefix) params.set('reference_prefix', prefix);
                    }
                }
            }
            const response = await fetch(`${API_BASE_URL}?${params.toString()}`, {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to fetch count');
            const data = await response.json();
            setTabCounts(prev => ({ ...prev, [tab]: data.count }));
        } catch (err) {
            console.error(err);
        }
    }, [module]);

    useEffect(() => {
        if (activeTab !== 'search_payable') {
            fetchPayables(activeTab);
        }
    }, [activeTab, fetchPayables]);

    useEffect(() => {
        fetchTabCount('pending_receipts');
        fetchTabCount('cash_office');
    }, [fetchTabCount]);

    const fetchPayableDetail = async (payable) => {
        try {
            const res = await fetch(`${API_BASE_URL}${payable.id}/`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                return { ...data, _payable_source: 'other' };
            }
            console.error('Failed to fetch payable details');
        } catch (err) {
            console.error('Error fetching payable details', err);
        }
        return payable;
    };

    const newPopoutKey = (list) => `${Date.now()}_${list.length}_${Math.random().toString(36).slice(2, 7)}`;

    const openFormPopout = (mode, payable) =>
        setFormPopouts(prev => [...prev, { key: newPopoutKey(prev), mode, payable }]);
    const openOfficePopout = (mode, payable) =>
        setOfficePopouts(prev => [...prev, { key: newPopoutKey(prev), mode, payable }]);

    const handleCreate = () => openFormPopout('add', null);

    const handleEdit = async (payable) => {
        const detail = await fetchPayableDetail(payable);
        if (activeTab === 'cash_office' || payable.module === 'OFFICE') {
            openOfficePopout('edit', detail);
        } else {
            openFormPopout('edit', detail);
        }
    };

    const handleView = async (payable) => {
        const detail = await fetchPayableDetail(payable);
        if (activeTab === 'cash_office' || payable.module === 'OFFICE') {
            openOfficePopout('view', detail);
        } else {
            openFormPopout('view', detail);
        }
    };

    const handleCloseForm = (key, refresh = false) => {
        setFormPopouts(prev => prev.filter(p => p.key !== key));
        if (refresh) {
            handleRefresh();
            fetchTabCount('cash_office');
            fetchTabCount('pending_receipts');
        }
    };

    const handleCreateOfficePayable = () => openOfficePopout('add', null);

    const handleCloseOfficeForm = (key, refresh = false) => {
        setOfficePopouts(prev => prev.filter(p => p.key !== key));
        if (refresh) {
            handleRefresh();
            fetchTabCount('cash_office');
            fetchTabCount('pending_receipts');
        }
    };

    const handleOpenContraModal = () => {
        setContraForm({ outgoing_bank: '', incoming_bank: '', date: '', amount: '', tax_invoice_no: '', tax_invoice_file: null });
        setShowContraModal(true);
    };

    const handleCloseContraModal = () => {
        setContraForm({ outgoing_bank: '', incoming_bank: '', date: '', amount: '', tax_invoice_no: '', tax_invoice_file: null });
        setShowContraModal(false);
    };

    const handleContraSubmit = async () => {
        if (!contraForm.outgoing_bank || !contraForm.incoming_bank || !contraForm.date || !contraForm.amount) {
            toast.error('Please fill all fields');
            return;
        }
        const isCash = contraForm.outgoing_bank === 'CASH';
        if (isCash && (!contraForm.tax_invoice_no || !contraForm.tax_invoice_file)) {
            toast.error('Tax invoice number and attachment are required for cash outgoing');
            return;
        }
        try {
            const token = localStorage.getItem('kk_token');
            const payload = new FormData();
            payload.append('outgoing_bank_account', contraForm.outgoing_bank);
            payload.append('incoming_bank_account', contraForm.incoming_bank);
            payload.append('date', contraForm.date);
            payload.append('amount', contraForm.amount);
            if (isCash) {
                payload.append('tax_invoice_no', contraForm.tax_invoice_no);
                payload.append('tax_invoice_file', contraForm.tax_invoice_file);
            }
            const res = await fetch('/api/admin/other-payables/contra-banking/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: payload
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Failed to create contra entry');
            }
            toast.success('Contra entry created successfully');
            handleCloseContraModal();
            handleRefresh();
            fetchTabCount('cash_office');
            fetchTabCount('pending_receipts');
        } catch (e) {
            toast.error(e.message);
        }
    };

    useEffect(() => {
        const fetchBankAccounts = async () => {
            try {
                const token = localStorage.getItem('kk_token');
                const res = await fetch('/api/admin/company-bank-accounts/', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setBankAccounts(data);
                }
            } catch (e) {
                console.error('Failed to fetch bank accounts:', e);
            }
        };
        fetchBankAccounts();
    }, []);

    const searchedPayables = React.useMemo(() => {
        let data = [...payables];
        if (searchQuery && searchField) {
            const query = searchQuery.toLowerCase();
            data = data.filter(p => {
                if (searchField === 'customer_name') {
                    const shortName = p.customer_short_name || '';
                    const fullName = p.customer_name || '';
                    return String(shortName).toLowerCase().includes(query) || String(fullName).toLowerCase().includes(query);
                }
                const val = p[searchField];
                return val !== null && val !== undefined && String(val).toLowerCase().includes(query);
            });
        }
        return data;
    }, [payables, searchQuery, searchField]);

    const sortableValue = useCallback((item, key) => {
        if (key === 'total_amount') {
            return parseFloat(item.total_amount || 0);
        }
        if (key === 'vendor_name') {
            if (item.vendor_staff_type === 'STAFF') {
                return item.staff_name || '';
            }
            return item.vendor_short_name || item.vendor_name || '';
        }
        if (key === 'created_at') {
            const t = item.created_at ? new Date(item.created_at).getTime() : NaN;
            return Number.isNaN(t) ? null : t;
        }
        if (key === 'approved_date') {
            const raw = item.status === 'CLIENT PAID' ? item.action_date : item.approved_stage_3_at;
            const t = raw ? new Date(raw).getTime() : NaN;
            return Number.isNaN(t) ? null : t;
        }
        return item[key];
    }, []);

    const defaultSortConfig = activeTab === 'pending_receipts'
        ? { key: 'approved_date', direction: 'descending' }
        : { key: 'created_at', direction: 'descending' };

    const { items: filteredPayables, requestSort, getSortIndicator } = useSortableTable(
        searchedPayables,
        defaultSortConfig,
        sortableValue,
        activeTab
    );

    return (
        <div className="list-container">
            <div className="list-header">
                <h2>{moduleLabel} Payables</h2>
                <button onClick={handleRefresh} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', marginLeft: 'auto', marginRight: 8, transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
                {hasPermission(permissionModule, 'can_create') && (
                    <>
                        {!hideNewPayable && (
                            <button className="btn btn-primary" onClick={handleCreate}>
                                New Payable
                            </button>
                        )}
                        {module !== 'PERMIT' && (
                            <button className="btn" onClick={handleCreateOfficePayable} style={{ marginLeft: '8px', backgroundColor: 'green', borderColor: 'green', color: 'white' }}>
                                + Cash/Office Payable
                            </button>
                        )}    
                        {module === 'ACCOUNTS' && (
                            <button className="btn" onClick={handleOpenContraModal} style={{ marginLeft: '8px', backgroundColor: '#6f42c1', borderColor: '#6f42c1', color: 'white' }}>
                                + Contra Banking
                            </button>
                        )}
                    </>
                )}
            </div>

            <div className="custom-tabs-container" style={{ marginBottom: 20 }}>
                {module === 'ACCOUNTS' && (
                    <button
                        className={`custom-tab ${activeTab === 'cash_office' ? 'active' : ''}`}
                        onClick={() => { activeTab === 'cash_office' ? fetchPayables('cash_office') : setActiveTab('cash_office'); }}
                    >
                        Pending Approvals ({tabCounts.cash_office})
                    </button>
                )}
                {module !== 'ACCOUNTS' && (
                    <button
                        className={`custom-tab ${activeTab === 'all' ? 'active' : ''}`}
                        onClick={() => { activeTab === 'all' ? fetchPayables('all') : setActiveTab('all'); }}
                    >
                        Pending Approvals ({tabCounts.all})
                    </button>
                )}
                <button
                    className={`custom-tab ${activeTab === 'pending_receipts' ? 'active' : ''}`}
                    onClick={() => { activeTab === 'pending_receipts' ? fetchPayables('pending_receipts') : setActiveTab('pending_receipts'); }}
                >
                    Pending Receipts ({tabCounts.pending_receipts})
                </button>
                <button
                    className={`custom-tab ${activeTab === 'search_payable' ? 'active' : ''}`}
                    onClick={() => { activeTab === 'search_payable' ? handleRefresh() : setActiveTab('search_payable'); setPayables([]); setLoading(false); setError(null); setSearchField(''); setSearchQuery(''); }}
                >
                    Search Payable
                </button>
                {module !== 'ACCOUNTS' && (
                    <button
                        className={`custom-tab ${activeTab === 'cash_office' ? 'active' : ''}`}
                        onClick={() => { activeTab === 'cash_office' ? fetchPayables('cash_office') : setActiveTab('cash_office'); }}
                    >
                        Cash/Office ({tabCounts.cash_office})
                    </button>
                )}
            </div>

            {activeTab !== 'search_payable' && (
                <div className="list-toolbar" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{ flex: '0 0 200px' }}>
                        <Select2Dropdown
                            options={searchOptions}
                            value={searchField}
                            onChange={(val) => setSearchField(val)}
                            valueKey="id"
                            displayKey="text"
                            placeholder="Search By..."
                        />
                    </div>
                    <div style={{ flex: '0 0 400px' }}>
                        <input
                            type="text"
                            placeholder={searchField ? `Enter ${searchOptions.find(o => o.id === searchField)?.text}...` : "Select a field first"}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                            disabled={!searchField}
                            style={{ width: '100%', height: '38px', padding: '6px 12px', border: '1px solid #ced4da', borderRadius: '4px' }}
                        />
                    </div>
                    {searchField && (
                        <button
                            className="btn btn-secondary"
                            onClick={() => { setSearchField(''); setSearchQuery(''); }}
                            title="Clear Search"
                        >
                            <i className="fa fa-times"></i> Clear Search
                        </button>
                    )}
                </div>
            )}

            {activeTab === 'search_payable' && (
                <div className="list-toolbar" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{ flex: '0 0 200px' }}>
                        <Select2Dropdown
                            options={searchOptions}
                            value={searchPayableField}
                            onChange={(val) => setSearchPayableField(val)}
                            valueKey="id"
                            displayKey="text"
                            placeholder="Search By..."
                        />
                    </div>
                    <div style={{ flex: '0 0 400px' }}>
                        <input
                            type="text"
                            placeholder={searchPayableField ? `Enter ${searchOptions.find(o => o.id === searchPayableField)?.text}...` : "Select a field first"}
                            value={searchPayableQuery}
                            onChange={(e) => setSearchPayableQuery(e.target.value)}
                            className="search-input"
                            disabled={!searchPayableField}
                            style={{ width: '100%', height: '38px', padding: '6px 12px', border: '1px solid #ced4da', borderRadius: '4px' }}
                        />
                    </div>
                    <button
                        className="btn btn-primary"
                        disabled={!searchPayableField || !searchPayableQuery}
                        onClick={() => {
                            const params = { field: searchPayableField, query: searchPayableQuery };
                            setLastSearchPayableParams(params);
                            setSearchPayableHasSearched(true);
                            fetchPayables('search_payable', params);
                        }}
                        style={{ height: 38, padding: '6px 16px' }}
                    >
                        <i className="fa fa-search"></i> Search
                    </button>
                    {searchPayableField && (
                        <button
                            className="btn btn-secondary"
                            onClick={() => {
                                setSearchPayableField('');
                                setSearchPayableQuery('');
                                setLastSearchPayableParams({ field: '', query: '' });
                                setSearchPayableHasSearched(false);
                                setPayables([]);
                            }}
                            style={{ height: 38, padding: '6px 16px' }}
                        >
                            <i className="fa fa-times"></i> Clear
                        </button>
                    )}
                </div>
            )}

            {loading ? <div>Loading...</div> : error ? <div className="error">{error}</div> : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="list-table">
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('reference_no')} style={{ cursor: 'pointer' }}>Ref No {getSortIndicator('reference_no')}</th>
                            <th onClick={() => requestSort('job_number')} style={{ cursor: 'pointer' }}>Job ID {getSortIndicator('job_number')}</th>
                            <th onClick={() => requestSort('bl_number')} style={{ cursor: 'pointer' }}>BL No {getSortIndicator('bl_number')}</th>
                            <th onClick={() => requestSort('created_at')} style={{ cursor: 'pointer' }}>Posted Date {getSortIndicator('created_at')}</th>
                            <th onClick={() => requestSort('idf_number')} style={{ cursor: 'pointer' }}>IDF {getSortIndicator('idf_number')}</th>
                            <th onClick={() => requestSort('customer_short_name')} style={{ cursor: 'pointer' }}>Customer {getSortIndicator('customer_short_name')}</th>
                            <th onClick={() => requestSort('vendor_name')} style={{ cursor: 'pointer' }}>{activeTab === 'cash_office' ? 'Vendor/Staff' : 'Vendor'} {getSortIndicator('vendor_name')}</th>
                            <th onClick={() => requestSort('total_amount')} style={{ cursor: 'pointer' }}>Amount {getSortIndicator('total_amount')}</th>
                            <th onClick={() => requestSort('status')} style={{ cursor: 'pointer' }}>Status {getSortIndicator('status')}</th>
                            {activeTab === 'pending_receipts' && <th onClick={() => requestSort('approved_date')} style={{ cursor: 'pointer' }}>Approved Date {getSortIndicator('approved_date')}</th>}
                            <th onClick={() => requestSort('action_by_name')} style={{ cursor: 'pointer' }}>Approved/Rejected By {getSortIndicator('action_by_name')}</th>
                            <th onClick={() => requestSort('rejection_reason')} style={{ cursor: 'pointer' }}>Rejection Reason {getSortIndicator('rejection_reason')}</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPayables.map(payable => (
                            <tr key={payable.id}>
                                <td style={{ fontWeight: '600', color: '#6c757d', whiteSpace: 'nowrap' }}>{payable.reference_no || '-'}</td>
                                <td style={{ fontWeight: 'bold', color: '#007bff' }}>{payable.job_number || '-'}</td>
                                <td>{payable.bl_number || '-'}</td>
                                <td>
                                    <div style={{ fontWeight: '600' }}>{new Date(payable.created_at).toLocaleDateString('en-GB')}</div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>{new Date(payable.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase()}</div>
                                </td>
                                <td>{payable.idf_number || '-'}</td>
                                <td><strong>{payable.customer_short_name || payable.customer_name || '-'}</strong></td>
                                <td>{activeTab === 'cash_office' && payable.vendor_staff_type === 'STAFF' ? (payable.staff_name || '-') : (payable.vendor_short_name || payable.vendor_name || '-')}</td>
                                <td>
                                    {(() => {
                                        const invoiceCurrencyDifferent = payable.currency === 'TZS' && parseFloat(payable.usd_rate || 0) > 100;
                                        const hasOldItems = payable.items && payable.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && payable.items.some(item => !item.total_amount);
                                        const amount = parseFloat(payable.total_amount || 0);
                                        const displayAmount = invoiceCurrencyDifferent && hasOldItems && payable.usd_rate
                                            ? amount * parseFloat(payable.usd_rate)
                                            : amount;
                                        return displayAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + payable.currency;
                                    })()}
                                </td>
                                <td>
                                    <span style={{
                                        padding: '5px 12px',
                                        borderRadius: '20px',
                                        fontWeight: '500',
                                        fontSize: '0.85em',
                                        display: 'inline-block',
                                        textAlign: 'center',
                                        minWidth: '100px',
                                        backgroundColor:
                                            payable.status === 'PENDING' ? '#fff3cd' :
                                            payable.status === 'APPROVED 1' ? '#cce5ff' :
                                            payable.status === 'APPROVED 2' ? '#d1ecf1' :
                                            payable.status === 'APPROVED 3' ? '#d4edda' :
                                            payable.status === 'CLIENT PAID' ? '#d4edda' :
                                            payable.status === 'REJECTED' ? '#f8d7da' :
                                            '#e2e3e5',
                                        color:
                                            payable.status === 'PENDING' ? '#856404' :
                                            payable.status === 'APPROVED 1' ? '#004085' :
                                            payable.status === 'APPROVED 2' ? '#0c5460' :
                                            payable.status === 'APPROVED 3' ? '#155724' :
                                            payable.status === 'CLIENT PAID' ? '#155724' :
                                            payable.status === 'REJECTED' ? '#721c24' :
                                            '#383d41',
                                        border: '1px solid transparent'
                                    }}>
                                        {STATUS_LABELS[payable.status] || payable.status}
                                    </span>
                                </td>
                                {activeTab === 'pending_receipts' && (
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {(() => {
                                            const raw = payable.status === 'CLIENT PAID'
                                                ? payable.action_date
                                                : payable.approved_stage_3_at;
                                            return raw ? new Date(raw).toLocaleDateString('en-GB') : '-';
                                        })()}
                                    </td>
                                )}
                                <td>{payable.action_by_name || '-'}</td>
                                <td style={{ color: 'red' }}>{activeTab === 'pending_receipts' ? (payable.receipt_rejection_reason || '-') : (payable.rejection_reason || '-')}</td>
                                <td>
                                    <button className="btn-icon" onClick={() => handleView(payable)} title="View">
                                        <i className="fa fa-eye"></i>
                                    </button>
                                    {hasPermission(permissionModule, 'can_edit') && (
                                        (payable.status === 'REJECTED') ||
                                        (activeTab === 'pending_receipts') ||
                                        (activeTab === 'cash_office' && payable.status === 'REJECTED')
                                    ) && (
                                        <button className="btn-icon" onClick={() => handleEdit(payable)} title="Edit">
                                            <i className="fa fa-pencil"></i>
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {filteredPayables.length === 0 && (
                            <tr><td colSpan="12" className="text-center">
                                {activeTab === 'search_payable' && !searchPayableHasSearched
                                    ? 'Enter search criteria and click Search to view results.'
                                    : 'No records found.'}
                            </td></tr>
                        )}
                    </tbody>
                    </table>
                </div>
            )}

            {formPopouts.map(p => (
                <PopoutWindow
                    key={p.key}
                    title={`${p.mode === 'view' ? 'View' : p.mode === 'edit' ? 'Edit' : 'New'} ${moduleLabel} Payable`}
                    name={`${module.toLowerCase()}_payable_form_${p.key}`}
                    toastContainerId={`payable-popout-toast-${p.key}`}
                    onClose={() => handleCloseForm(p.key, false)}
                >
                    <OtherPayablesForm
                        mode={p.mode}
                        receiptUploadOnly={activeTab === 'pending_receipts'}
                        initialData={p.payable}
                        onClose={() => handleCloseForm(p.key, false)}
                        onSave={() => handleCloseForm(p.key, true)}
                        module={module}
                        popoutMode={true}
                        toastContainerId={`payable-popout-toast-${p.key}`}
                    />
                </PopoutWindow>
            ))}

            {officePopouts.map(p => (
                <PopoutWindow
                    key={p.key}
                    title={`${p.mode === 'view' ? 'View' : p.mode === 'edit' ? 'Edit' : 'New'} Office Payable`}
                    name={`${module.toLowerCase()}_office_payable_form_${p.key}`}
                    toastContainerId={`office-payable-popout-toast-${p.key}`}
                    onClose={() => handleCloseOfficeForm(p.key, false)}
                >
                    <OfficePayablesForm
                        mode={p.mode}
                        initialData={p.payable}
                        onClose={() => handleCloseOfficeForm(p.key, false)}
                        onSave={() => handleCloseOfficeForm(p.key, true)}
                        module={module}
                        adminForTransporter={!!p.payable?.transporter}
                        popoutMode={true}
                        toastContainerId={`office-payable-popout-toast-${p.key}`}
                        receiptUploadOnly={activeTab === 'pending_receipts'}
                    />
                </PopoutWindow>
            ))}

            {showContraModal && (
                <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div className="dialog-container" style={{ width: '500px', maxWidth: '95%', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                        <div className="dialog-header" style={{ padding: '15px 20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#6f42c1', color: '#fff', borderRadius: '8px 8px 0 0' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Contra Banking</h3>
                            <button onClick={handleCloseContraModal} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer' }}>&times;</button>
                        </div>
                        <div className="dialog-body" style={{ padding: '20px' }}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 500 }}>Outgoing Bank Account *</label>
                                <Select2Dropdown
                                    options={[
                                        { id: '', text: 'Select Outgoing Bank' },
                                        { id: 'CASH', text: 'CASH' },
                                        ...bankAccounts.map(acc => ({ id: acc.id, text: acc.short_name || `${acc.bank_name} - ${acc.account_number}` }))
                                    ]}
                                    value={contraForm.outgoing_bank}
                                    onChange={val => setContraForm({ ...contraForm, outgoing_bank: val, incoming_bank: '' })}
                                    placeholder="Select Outgoing Bank"
                                    displayKey="text"
                                    valueKey="id"
                                />
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 500 }}>Incoming Bank Account *</label>
                                <Select2Dropdown
                                    options={[
                                        { id: '', text: 'Select Incoming Bank' },
                                        ...bankAccounts
                                            .filter(acc => acc.id !== parseInt(contraForm.outgoing_bank))
                                            .map(acc => ({ id: acc.id, text: acc.short_name || `${acc.bank_name} - ${acc.account_number}` }))
                                    ]}
                                    value={contraForm.incoming_bank}
                                    onChange={val => setContraForm({ ...contraForm, incoming_bank: val })}
                                    placeholder="Select Incoming Bank"
                                    displayKey="text"
                                    valueKey="id"
                                    disabled={!contraForm.outgoing_bank}
                                />
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 500 }}>Outgoing Date *</label>
                                <input
                                    type="date"
                                    value={contraForm.date}
                                    onChange={e => setContraForm({ ...contraForm, date: e.target.value })}
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                                />
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 500 }}>Amount *</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={formatAmountInput(contraForm.amount)}
                                    onChange={e => {
                                        const raw = e.target.value.replace(/[^0-9.]/g, '');
                                        const parts = raw.split('.');
                                        const cleaned = parts.length > 2
                                            ? `${parts[0]}.${parts.slice(1).join('')}`
                                            : raw;
                                        setContraForm({ ...contraForm, amount: cleaned });
                                    }}
                                    placeholder="0.00"
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                                />
                            </div>
                            {contraForm.outgoing_bank === 'CASH' && (
                                <>
                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 500 }}>Tax Invoice Number *</label>
                                        <input
                                            type="text"
                                            value={contraForm.tax_invoice_no}
                                            onChange={e => setContraForm({ ...contraForm, tax_invoice_no: e.target.value })}
                                            placeholder="Enter tax invoice number"
                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                                        />
                                    </div>
                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 500 }}>Tax Invoice Attachment *</label>
                                        <input
                                            type="file"
                                            onChange={e => setContraForm({ ...contraForm, tax_invoice_file: e.target.files[0] || null })}
                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="dialog-footer" style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button onClick={handleContraSubmit} style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', background: '#6f42c1', color: '#fff', cursor: 'pointer' }}>Save</button>
                            <button onClick={handleCloseContraModal} style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #ced4da', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OtherPayablesList;
